import { isNonEmptyObject } from '@sgme/fp';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { IdType, ReloadFormRequest } from 'epic/common-form/ReloadFormRequest';
import type { OrdersBasketPersistedForm } from 'models/forms/OrdersBasketPersistedForm';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ProcessingChain } from 'models/ProcessingChain';
import { ProductVariationBase } from 'models/productsReferential';
import type { ThunkDispatch } from 'redux-thunk';
import type { Action } from 'state/actions';
import type { DataById } from 'state/formData/formData.model';
import { verifyQuoteImportIdentifiers } from 'state/orders-basket/helpers';
import { bookingRequested } from 'state/quotes/booking/booking.actions';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { isUserPreferencesPrivateMode } from 'state/selectors';
import type { ExtraArgument, Thunk } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { removeIdenticalKeyValues } from 'state/utils/collection';
import { OrdersBasketProductSelectorOption } from 'state/workspaces/ordersBasket/ordersBasket.model';
import {
  ordersBasketTabContentInitialized,
  ordersBasketTabContentPersistenceRequested,
} from 'state/workspaces/ordersBasket/tabContent/tabContent.actions';
import {
  getOrdersBasketTabIdByFormId,
  getOrdersBasketWorkspaceTabContentById,
  getOrdersBasketWorkspaceTabContentForPersistenceById,
  getOrdersBasketWorkspaceTabIdByProductSubType,
} from 'state/workspaces/ordersBasket/tabContent/tabContent.selectors';
import {
  workspaceTabActivated,
  workspaceTabCreated,
  workspaceTabPersistRequested,
} from 'state/workspaces/workspace.actions';
import { assert } from 'utils/assert/base';
import type { HttpError } from 'utils/http.old/httpClient.model';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
import { getToastUtils } from 'utils/toast';

import {
  newOrdersBasketFormRequested,
  ordersBasketAllBookingResultsReceived,
  ordersBasketAllFormsSelected,
  ordersBasketAllFormsUnselected,
  ordersBasketFormDeleted,
  ordersBasketFormDeletionRequested,
  ordersBasketFormInitialized,
  ordersBasketFormMetadataPersistenceRequested,
  ordersBasketFormMetadataRemovalRequested,
  ordersBasketFormQuoteCanceled,
  ordersBasketFormQuoted,
  ordersBasketFormQuoteFailed,
  ordersBasketFormQuoteRequested,
  ordersBasketFormSelectionToggled,
  ordersBasketFormTradeAuthGranted,
  ordersBasketFormTraded,
  ordersBasketFormTradedWithWarning,
  ordersBasketFormTradeFailed,
  ordersBasketFormTradeRejected,
  ordersBasketFormTradeRequested,
  ordersBasketFormUpdated,
  ordersBasketFormUpdateRequested,
  ordersBasketFormValueChanged,
  ordersBasketImportFailed,
  ordersBasketImportReceived,
  ordersBasketImportRequested,
  ordersBasketImportWrongProductReceived,
  ordersBasketPersistedFormFetched,
  ordersBasketPersistedFormFetchFailed,
} from './actions';
import type { OrdersBasketQuoteResponse, OrdersBasketTradeResponse } from './models';
import {
  getOrdersBasketFieldValue,
  getOrdersBasketFormDirtyParameters,
  getOrdersBasketFormMetadata,
  getOrdersBasketFormUpdatingParameters,
  getOrdersBasketProductVariation,
  getOrdersBasketQuoteIdByFormId,
  getOrdersBasketSelectedFormsIdByProductVariation,
  getQuoteResponseById,
  getTradeAuthorizationResultById,
  isEveryOrdersBasketSelectableFormSelectedByProductVariation,
  isOrdersBasketFormBookWithExecutionEnabled,
  isOrdersBasketFormWithWarning,
} from './selectors';

const importFailedTitle = 'toast.import.failed';

export function ordersBasketProductSelectedThunk(
  selectedProductOption: OrdersBasketProductSelectorOption,
): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    if (selectedProductOption.productSubType === undefined) {
      getFrontLogger().error('Product SubType is required to select an Orders Basket product');
      return;
    }
    const existingTabId = getOrdersBasketWorkspaceTabIdByProductSubType(getState(), {
      productSubType: selectedProductOption.productSubType,
      productType: selectedProductOption.productType,
    });

    if (existingTabId) {
      dispatch(workspaceTabActivated(existingTabId, 'ordersBasket', undefined));
    } else {
      const newTabId = getNewGuid();
      dispatch(
        workspaceTabCreated(newTabId, 'ordersBasket', undefined, selectedProductOption.productSubTypeLabel || ''),
      );
      dispatch(
        ordersBasketTabContentInitialized(
          newTabId,
          selectedProductOption.productSubType,
          undefined,
          selectedProductOption.productType,
        ),
      );
      persistTabContent(newTabId, getState, dispatch);
    }
  };
}

export function newOrdersBasketFormRequestedThunk(
  quoteId: string,
  wrapper: string,
  productType: string,
  productSubType: string,
  tabName: string,
  processingChain?: ProcessingChain,
  pricerRfqId?: string,
  productId?: string,
): Thunk<void> {
  return (dispatch, getState, { getNewGuid, getTodayString }) => {
    const existingTabId = getOrdersBasketWorkspaceTabIdByProductSubType(getState(), { productSubType, productType });
    const newTabId = getNewGuid();

    if (existingTabId) {
      dispatch(workspaceTabActivated(existingTabId, 'ordersBasket', undefined));
    } else {
      dispatch(workspaceTabCreated(newTabId, 'ordersBasket', undefined, tabName));
      dispatch(ordersBasketTabContentInitialized(newTabId, productSubType, wrapper, productType));
    }

    const tabId = existingTabId ?? newTabId;
    const temporaryNegotiationId = getNewGuid();
    dispatch(
      newOrdersBasketFormRequested(
        productId ?? quoteId,
        productId ? IdType.ProductId : IdType.Guess,
        getTodayString(),
        temporaryNegotiationId,
        tabId,
        wrapper,
        productType,
        productSubType,
        processingChain,
        pricerRfqId,
      ),
    );
  };
}

export function ordersBasketFormInitializedThunk(
  tabId: string,
  temporaryNegotiationId: string,
  form: StaticFormResult,
): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(ordersBasketFormInitialized(tabId, temporaryNegotiationId, form));
    persistFormMetadata(form.negotiationId!, getState, dispatch);
    persistTabContent(tabId, getState, dispatch);
  };
}

export function ordersBasketAllFormsSelectionToggledThunk(productVariation: ProductVariationBase): Thunk<void> {
  return (dispatch, getState) => {
    const appState = getState();
    const isEveryFormSelected = isEveryOrdersBasketSelectableFormSelectedByProductVariation(appState, productVariation);
    if (isEveryFormSelected) {
      dispatch(ordersBasketAllFormsUnselected(productVariation));
    } else {
      dispatch(ordersBasketAllFormsSelected(productVariation));
    }
  };
}

export function ordersBasketAllFormsUnselectedThunk(productVariation: ProductVariationBase): Thunk<void> {
  return dispatch => {
    dispatch(ordersBasketAllFormsUnselected(productVariation));
  };
}

export function ordersBasketFormDeletionRequestedThunk(formId: string): Thunk<void> {
  return (dispatch, getState) => {
    const appState = getState();
    const tabId = getOrdersBasketTabIdByFormId(appState, formId);
    if (tabId) {
      dispatch(ordersBasketFormDeletionRequested(tabId, formId));
      dispatch(ordersBasketRemoveTabThunk(tabId));
    }
  };
}

export function ordersBasketFormDeletedThunk(formId: string): Thunk<void> {
  return (dispatch, getState) => {
    const appState = getState();
    const tabId = getOrdersBasketTabIdByFormId(appState, formId);
    if (tabId) {
      dispatch(ordersBasketFormDeleted(tabId, formId));
      dispatch(ordersBasketFormMetadataRemovalRequested(formId));

      persistTabContent(tabId, getState, dispatch);
    }
  };
}

// TODO: unit test
export function ordersBasketRemoveTabThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const { formIds } = getOrdersBasketWorkspaceTabContentById(state, tabId)!;
    const isEmptyTab = !formIds || formIds.length === 0;

    if (!isEmptyTab) {
      return;
    }

    dispatch(workspaceTabPersistRequested(tabId, 'ordersBasket'));
  };
}

export function ordersBasketSelectedFormsDeletionRequestedThunk(productVariation: ProductVariationBase): Thunk<void> {
  return (dispatch, getState) => {
    const appState = getState();
    const formIds = getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation);
    formIds.forEach(formId => {
      dispatch(ordersBasketFormSelectionToggled(formId));
      dispatch(ordersBasketFormDeletionRequestedThunk(formId));
    });
  };
}

export function ordersBasketSelectedFormsExecutionRequestedThunk(productVariation: ProductVariationBase): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const appState = getState();
    const formIds = getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation);
    if (!formIds || formIds.length === 0) {
      return;
    }
    formIds.forEach(formId => {
      const canBookWithExecution = isOrdersBasketFormBookWithExecutionEnabled(appState, formId);
      if (canBookWithExecution) {
        const tabId = getOrdersBasketTabIdByFormId(appState, formId);
        const quoteId = getNewGuid();
        const frontReconciliationId = getNewGuid();
        if (tabId) {
          dispatch(bookingRequested(tabId, formId, quoteId, frontReconciliationId, 'ordersBasket'));
        }
      } else {
        dispatch(ordersBasketFormQuoteRequested(formId));
      }
      dispatch(ordersBasketFormSelectionToggled(formId));
    });
  };
}

export function ordersBasketSelectedFormsCancellationRequestedThunk(
  productVariation: ProductVariationBase,
): Thunk<void> {
  return (dispatch, getState) => {
    const appState = getState();
    const formIds = getOrdersBasketSelectedFormsIdByProductVariation(appState, productVariation);
    formIds.forEach(formId => {
      const quoteId = getOrdersBasketQuoteIdByFormId(appState, formId);
      dispatch(ordersBasketFormSelectionToggled(formId));
      dispatch(ordersBasketFormQuoteCanceled(formId, quoteId));
    });
  };
}

export function ordersBasketFormValueChangedThunk(formId: string, fieldId: string, value: unknown): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const originalValue = getOrdersBasketFieldValue(state, formId, fieldId);
    const newValueTranslated = value === undefined || value === '' ? null : value;

    if (originalValue === newValueTranslated) {
      return;
    }

    dispatch(ordersBasketFormValueChanged(formId, fieldId, newValueTranslated));

    const updatingParameters = getOrdersBasketFormUpdatingParameters(state, formId);
    if (updatingParameters === undefined || Object.keys(updatingParameters).length === 0) {
      dispatch(ordersBasketFormUpdateRequested(formId));
    }
  };
}

export function ordersBasketFormUpdatedThunk(
  formId: string,
  updatedParameters: DataById,
  result: StaticFormResult,
): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(ordersBasketFormUpdated(formId, updatedParameters, result));

    const state = getState();
    const dirtyParameters = getOrdersBasketFormDirtyParameters(state, formId);
    if (dirtyParameters === undefined) {
      return;
    }
    const remainingDirtyParameters = removeIdenticalKeyValues(dirtyParameters, updatedParameters);
    if (isNonEmptyObject(remainingDirtyParameters)) {
      dispatch(ordersBasketFormUpdateRequested(formId));
    }
  };
}

export function ordersBasketFormQuotedThunk(
  formId: string,
  quoteResponse: OrdersBasketQuoteResponse,
  shouldRequestTrade = true,
): Thunk<void> {
  return (dispatch, getState) => {
    // For RFB
    if (!shouldRequestTrade) {
      dispatch(ordersBasketFormQuoted(formId, quoteResponse, false));
      return;
    }

    // For RFQ
    const state = getState();
    const tradeAuthResult = getTradeAuthorizationResultById(state, formId);
    const productVariation = getOrdersBasketProductVariation(state, formId);
    const isPendingTradeAuth = tradeAuthResult === undefined;
    dispatch(ordersBasketFormQuoted(formId, quoteResponse, isPendingTradeAuth));

    if (tradeAuthResult?.status === 'Granted') {
      assert(
        quoteResponse.quoteId === tradeAuthResult.quoteId,
        'Quote Response and Trade Authorization should have same quoteId to execute',
      );
      dispatch(ordersBasketFormTradeRequested(formId, quoteResponse.quoteId, productVariation));
    }
  };
}

export function ordersBasketFormTradeAuthGrantedThunk(
  formId: string,
  tradeAuthResult: NextTradeAuthorizationResult,
  shouldRequestTrade: boolean = true,
): Thunk<void> {
  return (dispatch, getState) => {
    // For RFB
    if (!shouldRequestTrade) {
      dispatch(ordersBasketFormTradeAuthGranted(formId, tradeAuthResult, false));
      return;
    }

    // For RFQ
    const state = getState();
    const quoteResponse = getQuoteResponseById(state, formId);
    const isPendingQuote = quoteResponse === undefined;
    const productVariation = getOrdersBasketProductVariation(state, formId);

    dispatch(ordersBasketFormTradeAuthGranted(formId, tradeAuthResult, isPendingQuote));

    if (quoteResponse?.status === 'Quoted' && shouldRequestTrade) {
      assert(
        quoteResponse.quoteId === tradeAuthResult.quoteId,
        'Quote Response and Trade Authorization should have same quoteId to execute',
      );
      dispatch(ordersBasketFormTradeRequested(formId, tradeAuthResult.quoteId, productVariation));
    }
  };
}

export function ordersBasketFormTradedThunk(
  formId: string,
  tradeResponse: OrdersBasketTradeResponse,
  isBooking = false,
): Thunk<void> {
  return (dispatch, getState) => {
    // For RFB
    if (isBooking) {
      dispatch(ordersBasketFormTraded(formId, tradeResponse, true));

      return;
    }

    // For RFQ+RFE
    const hasWarning = isOrdersBasketFormWithWarning(getState(), formId);

    if (hasWarning) {
      dispatch(ordersBasketFormTradedWithWarning(formId, tradeResponse));
      persistFormMetadata(formId, getState, dispatch);
    } else {
      dispatch(ordersBasketFormTraded(formId, tradeResponse, false));
      persistFormMetadata(formId, getState, dispatch);

      setTimeout(() => {
        dispatch(ordersBasketFormDeletedThunk(formId));
      }, 5000);
    }
  };
}
export function ordersBasketFormAllBookingResultsReceivedThunk(formId: string): Thunk<void> {
  return (dispatch, _) => {
    dispatch(ordersBasketAllBookingResultsReceived(formId));
    setTimeout(() => {
      dispatch(ordersBasketFormDeletedThunk(formId));
    }, 5000);
  };
}

export function ordersBasketFormsImportThunk(identifiers: string[], productVariation: ProductVariationBase): Thunk {
  return (dispatch, getState, { getNewGuid, getTodayString }) => {
    const toast = getToastUtils(dispatch);
    const tabId = getOrdersBasketWorkspaceTabIdByProductSubType(getState(), productVariation);
    const identifiersValidation = verifyQuoteImportIdentifiers(identifiers);
    const state = getState();
    const isPrivateMode = isUserPreferencesPrivateMode(state);

    identifiersValidation.invalid.forEach(id => {
      toast.warn(
        {
          id: 'quote.import.discarded',
          values: { quoteId: id },
        },
        { id: importFailedTitle },
      );
    });

    const requests: ReloadFormRequest[] = identifiersValidation.valid.map(id => {
      return {
        id: id,
        idType: IdType.Guess,
        tradeDate: getTodayString(),
        isPrivateMode,
      };
    });

    if (tabId !== undefined && requests.length > 0) {
      toast.success(
        {
          id: 'quote.import.ordersBasket.requested',
          values: { nbOfQuotes: requests.length },
        },
        { id: 'toast.import.inProgress' },
      );
      requests.forEach(r => {
        const temporaryNegotiationId = getNewGuid();
        dispatch(ordersBasketImportRequested(r, productVariation, tabId, temporaryNegotiationId));
      });
    }
  };
}

export function ordersBasketImportReceivedThunk(
  form: StaticFormResult,
  identifier: string,
  productVariation: ProductVariationBase,
  temporaryNegotiationId: string,
): Thunk {
  return (dispatch, getState) => {
    const toast = getToastUtils(dispatch);
    const tabId = getOrdersBasketWorkspaceTabIdByProductSubType(getState(), productVariation);
    if (tabId !== undefined) {
      const productSubtypeOfForm = form.values.ProductSubtype;
      if (productSubtypeOfForm === productVariation.productSubType) {
        dispatch(ordersBasketImportReceived(tabId, temporaryNegotiationId, form.negotiationId!, form));
        persistFormMetadata(form.negotiationId!, getState, dispatch);
        persistTabContent(tabId, getState, dispatch);
      } else {
        toast.warn(
          {
            id: 'quote.import.wrongProduct',
            values: { quoteId: identifier },
          },
          { id: importFailedTitle },
        );
        dispatch(ordersBasketImportWrongProductReceived(tabId, temporaryNegotiationId));
      }
    }
  };
}

export function ordersBasketImportFailedThunk(
  tabId: string,
  temporaryFormId: string,
  identifier: string,
  error: Error,
): Thunk {
  return dispatch => {
    const toast = getToastUtils(dispatch);
    if ((error as any).status === 400) {
      toast.warn(
        {
          id: 'quote.import.invalidQuote',
          values: { quoteId: identifier },
        },
        { id: importFailedTitle },
      );
    } else if ((error as any).status === 404) {
      toast.warn(
        {
          id: 'quote.import.notFound',
          values: { quoteId: identifier },
        },
        { id: importFailedTitle },
      );
    } else {
      toast.warn(
        {
          id: 'quote.import.ordersBasket.failed',
          values: {
            quoteId: identifier,
          },
        },
        { id: importFailedTitle },
        false,
      );
    }

    dispatch(ordersBasketImportFailed(tabId, temporaryFormId));
  };
}
export function ordersBasketFormQuoteFailedThunk(formId: string, error?: ApiErrorMessage): Thunk<void> {
  return (dispatch, getState) => {
    const newVersion =
      error?.newVersion !== undefined && typeof error.newVersion === 'number'
        ? (error.newVersion as number)
        : undefined;
    dispatch(ordersBasketFormQuoteFailed(formId, newVersion));

    persistFormMetadata(formId, getState, dispatch);
  };
}

export function ordersBasketFormTradeFailedThunk(formId: string, error: ApiErrorMessage): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(ordersBasketFormTradeFailed(formId, error));
    persistFormMetadata(formId, getState, dispatch);
  };
}

export function ordersBasketFormTradeRejectedThunk(
  formId: string,
  tradeResponse: OrdersBasketTradeResponse,
): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(ordersBasketFormTradeRejected(formId, tradeResponse));
    persistFormMetadata(formId, getState, dispatch);
  };
}

export function ordersBasketPersistedFormFetchedThunk(formId: string, form: OrdersBasketPersistedForm): Thunk<void> {
  return dispatch => {
    dispatch(ordersBasketPersistedFormFetched(formId, form));

    if (form.metadata.status === 'EXECUTED') {
      setTimeout(() => {
        dispatch(ordersBasketFormDeletedThunk(formId));
      }, 5000);
    }
  };
}

export function ordersBasketPersistedFormFetchFailedThunk(formId: string, error: HttpError): Thunk<void> {
  return dispatch => {
    dispatch(ordersBasketPersistedFormFetchFailed(formId));
    if (error.status === 404) {
      dispatch(ordersBasketFormDeletedThunk(formId));
    }
  };
}

function persistFormMetadata(
  formId: string,
  getState: () => AppState,
  dispatch: ThunkDispatch<AppState, ExtraArgument, Action>,
): void {
  const metadata = getOrdersBasketFormMetadata(getState(), formId);
  if (metadata) {
    dispatch(ordersBasketFormMetadataPersistenceRequested(formId, metadata));
  }
}

function persistTabContent(
  tabId: string,
  getState: () => AppState,
  dispatch: ThunkDispatch<AppState, ExtraArgument, Action>,
): void {
  const tabContent = getOrdersBasketWorkspaceTabContentForPersistenceById(getState(), tabId);
  if (tabContent) {
    dispatch(ordersBasketTabContentPersistenceRequested(tabId, tabContent));
  }
}
